import React from 'react';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';
import TableSkeleton from './states/TableSkeleton';
import './CohortPanel.css';

/**
 * Cohort Analysis retention triangle (040-cohort-analysis).
 * Rows = signup buckets (month or Sunday-start week), columns = periods since signup,
 * cells = retention % over the exact active-user count (— when not yet observed).
 * Includes an "All Users" aggregate row on top.
 *
 * Props: { data, loading, error, onRetry, granularity, onGranularityChange }
 *   granularity: 'week' | 'weekly' | 'month' | 'monthly' (default monthly)
 */

const EM_DASH = '—';
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Heat-map thresholds (tunable). Kept in one place so every row is coloured identically.
const TIER_STRONG = 55;
const TIER_MODERATE = 40;

const isWeekly = (g) => g === 'week' || g === 'weekly';

const formatPct = (pct) => (pct == null ? EM_DASH : `${Number(pct).toFixed(1)}%`);

const tierFor = (pct) => {
  if (pct == null) return null;
  if (pct >= TIER_STRONG) return 'strong';
  if (pct >= TIER_MODERATE) return 'moderate';
  return 'weak';
};

const fmtDay = (d) => `${MON[d.getUTCMonth()]} ${d.getUTCDate()}`;

// Row label: monthly → 'YYYY-MM'; weekly → 'May 31 - Jun 6' from the week-start key.
const rowLabel = (key, weekly) => {
  if (!weekly) return key;
  const start = new Date(`${key}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return key;
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return `${fmtDay(start)} - ${fmtDay(end)}`;
};

const Cell = ({ cell, size, onClick }) => {
  const pct = cell ? cell.retentionPct : null;
  const tier = tierFor(pct);
  const clickable = !!onClick && pct != null;
  return (
    <td
      className={`au-cohort__cell${tier ? ` au-cohort__cell--${tier}` : ''}${clickable ? ' au-cohort__cell--clickable' : ''}`}
      title={pct == null
        ? undefined
        : `${cell.activeUsers} of ${size} users${clickable ? ' — click to see who' : ''}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    >
      {pct == null ? (
        EM_DASH
      ) : (
        <>
          <span className="au-cohort__pct">{formatPct(pct)}</span>
          <span className="au-cohort__count">{cell.activeUsers}</span>
        </>
      )}
    </td>
  );
};

const CohortPanel = ({ data, loading = false, error = '', onRetry, granularity = 'monthly', onGranularityChange, onCellClick }) => {
  const weekly = isWeekly(granularity);
  const cohorts = (data && data.cohorts) || [];
  const maxPeriod = (data && Number(data.maxPeriod)) || 0;
  const periodCols = Array.from({ length: maxPeriod + 1 }, (_, n) => n);
  const allUsers = data && data.allUsers;

  const setG = (g) => { if (onGranularityChange) onGranularityChange(g); };

  const body = () => {
    if (loading) return <TableSkeleton rows={6} />;
    if (error) {
      return (
        <ErrorState
          title="Couldn’t load cohort analysis"
          message="We couldn’t load the retention cohorts. Please try again."
          onRetry={onRetry}
        />
      );
    }
    if (cohorts.length === 0) {
      return (
        <EmptyState
          title="No cohorts to show"
          message={`No registered users fall in the selected ${weekly ? 'weeks' : 'months'} yet.`}
        />
      );
    }
    return (
      <div className="au-cohort__scroll">
        <table className="au-cohort" data-testid="au-cohort-table">
          <thead>
            <tr>
              <th className="au-cohort__rowhead">Cohort</th>
              <th className="au-cohort__size">Size</th>
              {periodCols.map((n) => (
                <th key={n} className="au-cohort__col">{weekly ? `Week ${n}` : `M${n}`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allUsers ? (
              <tr className="au-cohort__allrow">
                <td className="au-cohort__rowhead">All Users</td>
                <td className="au-cohort__size">{allUsers.size}</td>
                {periodCols.map((n) => (
                  <Cell key={n} cell={allUsers.periods[n]} size={allUsers.size} />
                ))}
              </tr>
            ) : null}
            {cohorts.map((c) => (
              <tr key={c.cohortMonth}>
                <td className="au-cohort__rowhead">{rowLabel(c.cohortMonth, weekly)}</td>
                <td
                  className={`au-cohort__size${onCellClick ? ' au-cohort__size--clickable' : ''}`}
                  onClick={onCellClick ? () => onCellClick(c.cohortMonth, 'all') : undefined}
                  role={onCellClick ? 'button' : undefined}
                  tabIndex={onCellClick ? 0 : undefined}
                  title={onCellClick ? 'See everyone in this cohort' : undefined}
                >
                  {c.cohortSize}
                </td>
                {periodCols.map((n) => (
                  <Cell
                    key={n}
                    cell={c.periods[n]}
                    size={c.cohortSize}
                    onClick={onCellClick ? () => onCellClick(c.cohortMonth, n) : undefined}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const unit = weekly ? 'week' : 'month';
  const unitCap = weekly ? 'Week' : 'Month';
  const example = weekly ? '“May 31 – Jun 6”' : '“Jan 2026”';

  return (
    <div className="au-panel">
      <div className="au-panel__head">
        <div>
          <h4 className="au-panel__title">User activity by cohort</h4>
          <span className="au-cohort__hint">% of each signup {unit} that came back and reopened the app</span>
        </div>
        <span className="au-spacer" />
        <div className="au-cohort__toggle" role="group" aria-label="Cohort granularity">
          <button type="button" className={weekly ? 'is-active' : ''} onClick={() => setG('week')}>Weekly</button>
          <button type="button" className={weekly ? '' : 'is-active'} onClick={() => setG('month')}>Monthly</button>
        </div>
      </div>

      <details className="au-cohort__help">
        <summary>How to read this chart</summary>
        <p>
          A <strong>cohort</strong> is a group of users bundled together by when they first
          registered. Each row is one such group — everyone who signed up in the same {unit}{' '}
          (for example {example}), and the <strong>Size</strong> column is how many people that is.
        </p>
        <p>
          Reading a row from left to right follows that same group over time.{' '}
          <strong>{unitCap} 0</strong> is the {unit} they signed up, <strong>{unitCap} 1</strong>{' '}
          is the following {unit}, and so on. Each cell shows the share of that group who opened the
          app again in that {unit} — so you can see how many people keep coming back, and where they
          tend to drop off. Because everyone opens the app when they register, {unitCap} 0 is
          normally close to 100%.
        </p>
        <p>
          The <strong>All Users</strong> row at the top blends every group together into one overall
          return-rate trend. A dash (<span className="au-cohort__dash">—</span>) means that {unit}{' '}
          hasn’t finished yet, so there is no number to show. Cells are shaded by how strong the
          return rate is:
        </p>
        <ul className="au-cohort__legend">
          <li><span className="au-cohort__swatch au-cohort__cell--strong" /> Strong — 55% or more came back</li>
          <li><span className="au-cohort__swatch au-cohort__cell--moderate" /> Moderate — 40% to 54%</li>
          <li><span className="au-cohort__swatch au-cohort__cell--weak" /> Weak — under 40%</li>
          <li><span className="au-cohort__dash">—</span> Not enough time has passed yet</li>
        </ul>
      </details>

      {body()}
    </div>
  );
};

export default CohortPanel;
